import { useMemo, useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { ChartTooltipCard } from "@/lib/dashboard/shared/ChartTooltip";
import { PaymentStatusCard } from "@/lib/dashboard/PaymentStatusCard";
import { ClientsAndTripsCard } from "@/lib/dashboard/ClientsAndTripsCard";
import { OverviewStatsCard } from "@/lib/dashboard/OverviewStatsCard";
import { useI18n } from "@/hooks/useI18n";
import { useAgencyDashboardStats } from "@/hooks/useAgencyDashboardStats";
import { AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, TrendingDown, TrendingUp, XCircle } from "lucide-react";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

/** Quante rate elencare nel tooltip di un giorno prima di riassumere le altre. */
const TOOLTIP_MAX_INSTALLMENTS = 5;

function formatPercent(value: number): string {
  return `${(value || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;
}

interface AgencyOverviewTabProps {
  period: AgencyDashboardPeriod;
}

export function AgencyOverviewTab({ period }: AgencyOverviewTabProps) {
  const { t } = useI18n();
  const { data, loading, error, refresh } = useAgencyDashboardStats(period);

  const [minLoadingDone, setMinLoadingDone] = useState(false);
  useEffect(() => {
    setMinLoadingDone(false);
    const timer = setTimeout(() => setMinLoadingDone(true), 500);
    return () => clearTimeout(timer);
  }, [period]);

  const isLoading = loading || !minLoadingDone;

  const chartData = useMemo(
    () =>
      (data?.timeseries.current ?? []).map((point) => ({
        date: point.date,
        value: point.value,
        delta: point.delta,
        installments: point.installments ?? [],
        installmentsCount: point.installments_count ?? point.installments?.length ?? 0,
      })),
    [data],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Tre card in fila, come nel contenuto vero: senza, al primo render la pagina
            saltava da un layout all'altro. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-10 w-24" />
                </div>
                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Grafico a tutta larghezza, come il contenuto vero */}
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32 mt-1" />
          </CardHeader>
          <CardContent className="h-72">
            <Skeleton className="h-full w-full rounded-lg" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {t("dashboard.overview.errorTitle")}
              </p>
              <p className="text-sm text-gray-500">
                {t("dashboard.overview.errorDescription")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh()}
              className="mt-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { kpis, timeseries } = data;
  const deltaPositive = (timeseries.delta_percent ?? 0) >= 0;

  return (
    <div className="space-y-4">
      {/*
        Tre card in fila, un terzo ciascuna: quante persone e quante pratiche, a che punto
        sono i soldi, e le statistiche derivate.

        Prima erano dieci tessere da un numero l'una. Ogni tessera rispondeva a una
        domanda diversa e nessuna la finiva, cosi' per capire una cosa sola bisognava
        leggerne tre e tenerle a mente insieme. Raggruppate, ogni card e' un discorso: un
        numero grande in cima -- la risposta -- e sotto la sua scomposizione.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
        <ClientsAndTripsCard
          clientsCount={kpis.connected_clients}
          activeCount={kpis.active_count}
          completedCount={kpis.completed_count}
          agenciesCount={kpis.agencies_count}
          advisorsCount={kpis.advisors_count}
        />

        <PaymentStatusCard
          paidAmount={kpis.paid_amount}
          pendingAmount={kpis.remaining_amount}
          overdueAmount={kpis.overdue_amount}
          totalAmount={kpis.total_amount}
          platformFeeDueAmount={kpis.platform_fee_due_total}
          platformFeeDueCount={kpis.platform_fee_due_count}
          isPlatformScope={data.meta.scope === "platform"}
          formatCurrency={formatCurrency}
        />

        <OverviewStatsCard
          completedCount={kpis.completed_count}
          activeCount={kpis.active_count}
          travelersCount={kpis.travelers_count}
          closedValueTotal={kpis.closed_value_total}
          avgValuePerItinerary={kpis.avg_value_per_itinerary}
          marginTotal={kpis.net_earnings_total}
          avgMarginPercent={kpis.avg_margin_percent}
          platformFeeTotal={kpis.platform_fee_total}
          formatCurrency={formatCurrency}
          formatPercent={formatPercent}
        />
      </div>

      {/*
        Il grafico occupa la riga intera: era stretto in due terzi per fare spazio al donut
        delle destinazioni, che raccontava una cosa diversa (dove si va) accanto a una
        serie storica (quanto si e' chiuso). Tolto il donut, l'andamento e' l'unica cosa
        che resta da leggere e puo' finalmente usare la larghezza che gli serve.
      */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            {t("dashboard.overview.trendTitle")}
            <InfoTooltip text={t("dashboard.overview.trendTitleInfo")} />
          </CardTitle>
          <div className="flex items-center gap-2 text-sm">
            <span
              className={deltaPositive ? "text-emerald-600" : "text-red-500"}
            >
              {deltaPositive ? (
                <TrendingUp className="inline h-4 w-4 mr-1" />
              ) : (
                <TrendingDown className="inline h-4 w-4 mr-1" />
              )}
              {timeseries.delta_amount >= 0 ? "+" : ""}
              {formatCurrency(timeseries.delta_amount)}
              {timeseries.delta_percent != null &&
                ` (${formatPercent(timeseries.delta_percent)})`}
            </span>
            <span className="text-gray-400">
              ·{" "}
              {t("dashboard.overview.periodDays", {
                days: String(
                  timeseries.current.length ? data.meta.period_days : 0,
                ),
              })}
            </span>
          </div>
        </CardHeader>
        <CardContent className="h-72">
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-gray-400">
              {t("dashboard.overview.noTrendData")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="agencyTrendFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d: string) =>
                    new Date(d).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                    })
                  }
                />
                <RechartsTooltip
                  cursor={{ stroke: "#f97316", strokeOpacity: 0.3 }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const point = payload[0].payload as (typeof chartData)[number];
                    // Le rate del giorno una per riga: chi ha pagato e quale rata. Oltre
                    // le prime TOOLTIP_MAX_INSTALLMENTS il tooltip coprirebbe il grafico.
                    const shown = point.installments.slice(0, TOOLTIP_MAX_INSTALLMENTS);
                    const hidden = point.installmentsCount - shown.length;
                    return (
                      <ChartTooltipCard
                        title={new Date(label as string).toLocaleDateString(
                          "it-IT",
                          { day: "2-digit", month: "long", year: "numeric" },
                        )}
                        rows={[
                          {
                            label: t("dashboard.overview.tooltipTotal"),
                            value: formatCurrency(point.value),
                            color: "#f97316",
                          },
                          ...shown.map((inst) => ({
                            label: `${inst.client_name || inst.itinerary_title || "—"} · ${
                              inst.label ||
                              t("dashboard.overview.tooltipInstallmentFallback", {
                                seq: String(inst.seq),
                              })
                            }`,
                            value: formatCurrency(inst.amount),
                          })),
                          ...(hidden > 0
                            ? [
                                {
                                  label: t("dashboard.overview.tooltipMoreInstallments", {
                                    count: String(hidden),
                                  }),
                                  value: "",
                                },
                              ]
                            : []),
                        ]}
                        footer={`+${formatCurrency(point.delta)} ${t("dashboard.overview.tooltipDelta")} · ${t(
                          "dashboard.overview.tooltipInstallmentsCount",
                          { count: String(point.installmentsCount) },
                        )}`}
                        footerClassName="text-emerald-600"
                      />
                    );
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#f97316"
                  strokeWidth={2}
                  fill="url(#agencyTrendFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AgencyOverviewTab;
